import { useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { omit } from 'lodash'

import { googleAddressPropTypes } from '../../../common/propTypes'
import useEnhancedFormik from '../../../hooks/useEnhancedFormik'
import useTranslations from '../../../i18n/useTranslations'
import {
  ADDRESS_STREET_KEYS,
  ADDRESS_COMPLEMENTARY_KEYS,
  addressDataToString,
} from '../../../context/business'

//NOTE: The postal code is retrieved from Google API even if user doesn't type it.
const MANDATORY_AUTOCOMPLETE_FIELDS = [
  'country',
  // 'postalCode',
  'city',
  'route',
  'streetNumber',
]

const formatConfirmedAddress = (addressData, t) => {
  const addressKeys = ADDRESS_STREET_KEYS.concat(ADDRESS_COMPLEMENTARY_KEYS)
  const noAddressData = omit(addressData, addressKeys)

  return {
    ...noAddressData,
    postalCode: {
      postalCode: addressData.postalCode,
      city: addressData.city,
    },
    address: addressDataToString(addressData, t),
  }
}

const addressSchema = Yup.object().shape({
  name: Yup.string().required(),
  phone: Yup.string().required(),
  email: Yup.string(),
  country: Yup.string().required(),
  postalCode: Yup.string().required(),
  city: Yup.string().required(),
  route: Yup.string().required(),
  streetNumber: Yup.string().required(),
  floor: Yup.string(),
  stair: Yup.string(),
  letter: Yup.string(),
  saveAddress: Yup.boolean(),
})

const addressInitialValues = {
  name: '',
  country: '',
  postalCode: '',
  phone: '',
  email: '',
  city: '',
  route: '',
  streetNumber: '',
  floor: '',
  stair: '',
  letter: '',
  saveAddress: false,
}

const useAddressEntryForm = (setIsAddressConfirmed, setConfirmedAddress) => {
  const t = useTranslations()

  //NOTE: we have to track autocomplete errors separately since passing the formik's props
  //to the mui Autocomplete makes it rerender on state changes and therefore lose the input value
  const [autocompleteErrors, setAutocompleteErrors] = useState([])

  const formik = useFormik({
    validationSchema: addressSchema,
    initialValues: addressInitialValues,
    onSubmit: async formValues => {
      const formattedAddress = formatConfirmedAddress(formValues, t)
      setConfirmedAddress(formattedAddress)
      setIsAddressConfirmed(true)
    },
  })

  const enhancedFormik = useEnhancedFormik(googleAddressPropTypes, formik, {})

  const handleChangeAutocompleteInput = () => {
    setAutocompleteErrors([])
  }

  const updateAutocompleteErrors = addressData => {
    const autocompleteErrors = !Boolean(addressData)
      ? []
      : MANDATORY_AUTOCOMPLETE_FIELDS.filter(
          fieldKey => !Boolean(addressData[fieldKey]),
        )
    setAutocompleteErrors(autocompleteErrors)
  }

  const handleChangeAutocompleteAddress = addressData => {
    if (addressData) {
      updateAutocompleteErrors(addressData)
      formik.setValues(values => ({
        ...values,
        ...addressData,
      }))
    }
  }

  return {
    handleChangeAutocompleteAddress,
    handleChangeAutocompleteInput,
    autocompleteErrors,
    formik,
    enhancedFormik,
    addressInitialValues,
  }
}

export default useAddressEntryForm
